// The game somebody sent us, on the board, with a transport.
//
// **Two things at once, and they can disagree.** The *snapshot* is the position
// the player was looking at — always readable, because it is a board rather
// than a sequence of instructions. The *log* is how they got there, and it only
// means anything against the rules that produced it, so on a build that has
// moved it replays partly or not at all. This shows both and says which is
// which: the transport walks the replay, and the moment the replay stops
// agreeing with the snapshot, the panel says so rather than letting the last
// frame stand in for what the player saw.

import { useEffect, useMemo, useRef, useState } from "react";

import { decode } from "src/engine/game/codec";
import { rebuildStructureMirror } from "src/engine/rules/industry/structures";
import type { GameState } from "src/engine/rules/commands";
import { gameOf } from "src/engine/api/createGame";
import { BoardCanvas, type BoardCanvasHandle } from "./BoardCanvas";
import { frameHash, replayLine, replayReport, type Replay } from "./feedbackReplay";
import { Btn, Notice, Panel, Pill, Slider, styled, Text, TOKENS } from "./kit";

/** What `/api/feedback/state` answers with. */
interface Fetched {
  state: unknown;
  log: unknown;
  hash: string | null;
}

/**
 * The canvas, behind everything.
 *
 * `zIndex: -1` is what keeps the page usable — the same arrangement the
 * gallery's board panel documents. Every handler is a no-op, so what the canvas
 * buys is the camera.
 */
const Behind = styled("div")({ position: "fixed", inset: 0, zIndex: -1 });

const Frame = styled(Panel)(({ theme }) => ({
  marginTop: theme.kit.space.sm,
  padding: theme.kit.space.md
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.kit.space.sm,
  marginBottom: theme.kit.space.sm
}));

const Scrub = styled("div")(({ theme }) => ({
  flex: 1,
  minWidth: 120,
  padding: `0 ${theme.kit.space.sm}px`
}));

/** One line of what the reducer refused, in the voice a log is read in. */
const Fault = styled(Text)({ display: "block" });

export function FeedbackBoard({
  id,
  onClose
}: {
  id: number;
  onClose: () => void;
}): React.ReactElement {
  const [fetched, setFetched] = useState<Fetched | null>(null);
  const [failed, setFailed] = useState(false);
  const [at, setAt] = useState(0);
  const [playing, setPlaying] = useState(false);
  const canvas = useRef<BoardCanvasHandle>(null);

  useEffect(() => {
    setFetched(null);
    setFailed(false);
    setAt(0);
    setPlaying(false);
    let live = true;
    void fetch(`/api/feedback/state?id=${id}`, { credentials: "same-origin" })
      .then(async (response) => {
        if (!response.ok) throw new Error(String(response.status));
        return (await response.json()) as Fetched;
      })
      .then((body) => live && setFetched(body))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [id]);

  /**
   * The snapshot, decoded.
   *
   * Through the game's own codec, which is the whole reason the report is
   * stored in that encoding: this is the same call `runSave.ts` makes to load
   * a run, so a board that reaches here is a board the game could play.
   */
  const reported = useMemo<GameState | null>(() => {
    if (!fetched?.state) return null;
    try {
      const state = decode(fetched.state) as GameState;
      // Derived state gets rebuilt rather than trusted: a report filed before
      // the structure mirrors existed decodes to a position without them, and
      // the replay below walks the whole log through move generation, which
      // reads both (§26 F5, §27). `loadRun` takes the same line.
      rebuildStructureMirror(state.position, state.industry);
      return state;
    } catch {
      return null;
    }
  }, [fetched]);

  const log = useMemo(
    () => (Array.isArray(fetched?.log) ? (fetched.log as unknown[]) : null),
    [fetched]
  );

  /**
   * The replay, computed once per report.
   *
   * Memoised because it walks the whole log through the reducer — a few
   * thousand commands is milliseconds, and doing it per render of a panel with
   * a slider on it is not.
   */
  const replay = useMemo<Replay | null>(
    () => (fetched ? replayReport(log, reported) : null),
    [fetched, log, reported]
  );

  const frames = replay?.frames ?? [];
  const last = Math.max(0, frames.length - 1);

  // **The snapshot wins at the end of the road.** Where the replay is exact the
  // two are the same board; where it is not, the final frame is a
  // reconstruction that ended somewhere else, and what the player actually saw
  // is the snapshot. So the last step of the transport shows the snapshot when
  // there is one, and the panel says which is on screen.
  const showingSnapshot = at >= last && reported !== null && replay?.matches === false;
  const shown = showingSnapshot ? reported : (frames[Math.min(at, last)] ?? reported);
  // A still board: its own `Game`, rebuilt when the frame on screen changes.
  const game = useMemo(() => (shown ? gameOf(shown) : null), [shown]);

  useEffect(() => {
    if (!playing) return;
    if (at >= last) {
      setPlaying(false);
      return;
    }
    const timer = setTimeout(() => setAt((was) => Math.min(last, was + 1)), 220);
    return () => clearTimeout(timer);
  }, [playing, at, last]);

  /**
   * Keep the camera in the free part of the page.
   *
   * The same mechanism the gallery's board uses: the canvas is drawn edge to
   * edge behind the page, so "centred" and "visible" stop being the same thing
   * the moment a column is laid over one side of it. `setViewInset` shifts the
   * projection rather than the CSS.
   */
  useEffect(() => {
    if (!shown) return;
    const apply = (): void => {
      const column = document.querySelector<HTMLElement>("[data-feedback-panel]");
      const covered = column ? column.getBoundingClientRect().right : 0;
      canvas.current?.setViewInset({
        left: Math.max(0, Math.round(covered)),
        right: 0,
        top: TOKENS.layout.adminBar,
        bottom: 0
      });
      canvas.current?.fitView();
    };
    const raf = requestAnimationFrame(apply);
    const timer = setTimeout(apply, 250);
    window.addEventListener("resize", apply);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      window.removeEventListener("resize", apply);
    };
  }, [shown]);

  if (failed) {
    return (
      <Notice tone="bad" data-testid="feedback-board-failed" style={{ marginTop: 8 }}>
        Could not read that board.{" "}
        <Btn small variant="ghost" onClick={onClose}>
          Close
        </Btn>
      </Notice>
    );
  }

  if (!fetched || !shown || !replay || !game) {
    return (
      <Text block tone="dim" size={12} data-testid="feedback-board-loading" style={{ marginTop: 8 }}>
        Opening the board…
      </Text>
    );
  }

  return (
    <>
      <Behind>
        <BoardCanvas
          ref={canvas}
          game={game}
          interactive={false}
        />
      </Behind>

      <Frame data-testid="feedback-board">
        <Row>
          <Btn
            small
            variant="ghost"
            data-testid="feedback-play"
            onClick={() => setPlaying((was) => !was)}
            disabled={last === 0}
          >
            {playing ? "Pause" : "Play"}
          </Btn>
          <Btn small variant="ghost" onClick={() => setAt(0)} disabled={at === 0}>
            Start
          </Btn>
          <Btn
            small
            variant="ghost"
            onClick={() => setAt((was) => Math.max(0, was - 1))}
            disabled={at === 0}
          >
            Back
          </Btn>
          <Btn
            small
            variant="ghost"
            onClick={() => setAt((was) => Math.min(last, was + 1))}
            disabled={at >= last}
          >
            Step
          </Btn>
          <Btn small variant="ghost" onClick={() => setAt(last)} disabled={at >= last}>
            End
          </Btn>
          <Scrub>
            <Slider
              size="small"
              data-testid="feedback-scrub"
              value={Math.min(at, last)}
              min={0}
              max={last}
              disabled={last === 0}
              onChange={(_, value) => {
                setPlaying(false);
                setAt(typeof value === "number" ? value : 0);
              }}
            />
          </Scrub>
          <Text role="display" tone="dim" size={11}>
            {Math.min(at, last)} / {last}
          </Text>
          <Btn small variant="ghost" data-testid="feedback-board-close" onClick={onClose}>
            Close
          </Btn>
        </Row>

        <Row>
          <Pill data-testid="feedback-frame-kind">
            <Text role="display" tone={showingSnapshot ? "brand" : "cream"} size={10}>
              {showingSnapshot ? "the reported board" : `replayed · ${frameHash(shown)}`}
            </Text>
          </Pill>
          {fetched.hash && (
            <Text role="display" tone="dim" size={10}>
              report {fetched.hash}
            </Text>
          )}
        </Row>

        {/* **What the replay could and could not do, in one line.** The panel
            is useless without it: a viewer that shows a board and says nothing
            about how much of the log produced it is a viewer that invites the
            reader to trust a reconstruction. */}
        <Text block size={11} data-testid="feedback-replay-line">
          {replayLine(replay, log?.length ?? 0)}
        </Text>

        {replay.faults.length > 0 && (
          <div>
            {replay.faults.slice(0, 6).map((fault) => (
              <Fault
                key={fault.index}
                role="display"
                tone="dim"
                size={10}
                data-testid="feedback-fault"
              >
                #{fault.index} {fault.type} — {fault.reason}
              </Fault>
            ))}
            {replay.faults.length > 6 && (
              <Text block tone="dim" size={10}>
                and {replay.faults.length - 6} more.
              </Text>
            )}
          </div>
        )}
      </Frame>
    </>
  );
}
